/**
 * <facet-filters-form> — progressive enhancement over a plain GET form.
 * Filter/sort changes are serialized, fetched with ?section_id= (Section
 * Rendering API) so results honor server-side filter counts and Markets
 * pricing, then swapped into the facets/grid without a full page reload.
 * Falls back to a normal navigation if fetch fails for any reason.
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.baseUrl = this.dataset.facetsUrl;
    this.sectionId = this.dataset.sectionId;

    this.form.addEventListener('input', this.debounce(() => this.onChange(), 300));
    this.form.addEventListener('change', (event) => {
      if (event.target.matches('[data-sort-select]')) this.onChange();
    });

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-facets-clear]')) {
        event.preventDefault();
        this.fetchAndRender(this.baseUrl);
        return;
      }
      const paginationLink = event.target.closest('.collection__pagination a');
      if (paginationLink) {
        event.preventDefault();
        this.fetchAndRender(paginationLink.href);
      }
    });

    window.addEventListener('popstate', () => this.fetchAndRender(window.location.href, false));

    // Only one filter dropdown open at a time, and close on outside click —
    // native <details>/<summary> handle the open/close toggle itself, this
    // just keeps multiple panels from stacking on top of each other.
    this.addEventListener(
      'toggle',
      (event) => {
        if (event.target.tagName !== 'DETAILS' || !event.target.open) return;
        this.querySelectorAll('details[data-facet]').forEach((details) => {
          if (details !== event.target) details.open = false;
        });
      },
      true
    );

    document.addEventListener('click', (event) => {
      if (this.contains(event.target)) return;
      this.querySelectorAll('details[data-facet][open]').forEach((details) => {
        details.open = false;
      });
    });
  }

  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn(...args), wait);
    };
  }

  onChange() {
    const params = new URLSearchParams(new FormData(this.form));
    const url = `${this.baseUrl}?${params.toString()}`;
    this.fetchAndRender(url);
  }

  async fetchAndRender(url, updateHistory = true) {
    try {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(fetchUrl.toString());
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const freshFacets = doc.querySelector('[data-facets]');
      const freshGrid = doc.querySelector('[data-product-grid]');
      const freshCount = doc.querySelector('.collection__count');
      const currentFacets = this.querySelector('[data-facets]');
      const currentGrid = this.querySelector('[data-product-grid]');
      const currentCount = this.querySelector('.collection__count');

      if (freshFacets && currentFacets) currentFacets.innerHTML = freshFacets.innerHTML;
      if (freshGrid && currentGrid) currentGrid.innerHTML = freshGrid.innerHTML;
      if (freshCount && currentCount) currentCount.textContent = freshCount.textContent;

      if (updateHistory) {
        const cleanUrl = new URL(url, window.location.origin);
        cleanUrl.searchParams.delete('section_id');
        window.history.pushState({}, '', cleanUrl.toString());
      }
    } catch (error) {
      window.location.href = url;
    }
  }
}

customElements.define('facet-filters-form', FacetFiltersForm);
